import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Card,
  CardActionArea,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PLAN_BLURBS, formatAmount, tierAccent } from "./billingCopy";

/**
 * Theme-aware plan card grid with the monthly/annual toggle — shared by the
 * plan-change wizard (existing subscribers) and the subscribe modal
 * (trials / new subscriptions, replacing the Stripe-hosted pricing table).
 *
 * `plans` is getBillingOverview's plans array (labels, prices by lookup key).
 */
const PlanCards = ({
  plans,
  interval,
  onIntervalChange,
  currentLevel,
  onSelect,
  disabled,
  // Emphasized tier (primary outline) — e.g. "plus" on the subscribe modal.
  // currentLevel's green treatment wins over this in the wizard.
  recommendedKey,
  // Optional short feature name (e.g. "Consolidation") — when set, the
  // recommended card gets an "Includes {recommendBadge}" chip on its title row.
  recommendBadge,
}) => (
  <>
    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={interval}
        onChange={(e, v) => v && onIntervalChange(v)}
        sx={{
          // Primary pop on the selected interval (alpha .18 dark / .10 light)
          "& .MuiToggleButton-root.Mui-selected": {
            color: "primary.main",
            fontWeight: 600,
            backgroundColor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.mode === "dark" ? 0.18 : 0.1,
              ),
            "&:hover": {
              backgroundColor: (theme) =>
                alpha(
                  theme.palette.primary.main,
                  theme.palette.mode === "dark" ? 0.26 : 0.16,
                ),
            },
          },
        }}
      >
        <ToggleButton value="monthly">Monthly</ToggleButton>
        <ToggleButton value="annual">Annual (save 10%)</ToggleButton>
      </ToggleButtonGroup>
    </Box>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
        gap: 2,
      }}
    >
      {plans?.map((plan) => {
        const isCurrent = plan.key === currentLevel;
        const isRecommended = !isCurrent && plan.key === recommendedKey;
        const amount =
          interval === "annual" ? plan.annualAmount : plan.monthlyAmount;
        const blurbs = PLAN_BLURBS[plan.key] || [];
        return (
          <Card
            key={plan.key}
            variant="outlined"
            sx={{
              textAlign: "left",
              // Current = green 2px; recommended = primary 2px; rest = 1px
              borderColor: isCurrent
                ? "success.main"
                : isRecommended
                  ? "primary.main"
                  : "divider",
              borderWidth: isCurrent || isRecommended ? 2 : 1,
            }}
          >
            <CardActionArea
              disabled={isCurrent || disabled}
              onClick={() => onSelect(plan.key)}
              data-testid={`billing-plan-card-${plan.key}`}
              sx={{ height: "100%", alignItems: "flex-start" }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ color: (theme) => tierAccent(plan.key, theme), fontWeight: 600 }}
                  >
                    {plan.label}
                  </Typography>
                  {isCurrent && (
                    <Chip label="Current" size="small" color="success" />
                  )}
                  {isRecommended && recommendBadge && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`Includes ${recommendBadge}`}
                      data-testid="billing-plan-card-recommend-badge"
                    />
                  )}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                  {formatAmount(amount) || "—"}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {" "}/ {interval === "annual" ? "yr" : "mo"}
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {plan.countedUserLimit
                    ? `${plan.countedUserLimit} full-access user${plan.countedUserLimit !== 1 ? "s" : ""}`
                    : "Unlimited full-access users"}
                  {" • audit users free"}
                </Typography>
                {/* Long lists collapse on small screens (stacked cards) */}
                <PlanFeatureList blurbs={blurbs} />
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  </>
);

/**
 * Collapses long feature lists behind an expander when the plan cards stack
 * on small screens; larger screens always show the full list.
 */
const PlanFeatureList = ({ blurbs }) => {
  const [expanded, setExpanded] = useState(false);
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const collapsedCount = 3;
  const collapsed = isSmall && !expanded && blurbs.length > collapsedCount;
  const items = collapsed ? blurbs.slice(0, collapsedCount) : blurbs;
  return (
    <Box component="ul" sx={{ pl: 2, mb: 0, mt: 1 }}>
      {items.map((b) => (
        <Typography component="li" variant="caption" key={b} sx={{ display: "list-item" }}>
          {b}
        </Typography>
      ))}
      {isSmall && blurbs.length > collapsedCount && (
        <Typography
          variant="caption"
          color="primary"
          sx={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? "Show less ▴" : "See all features ▾"}
        </Typography>
      )}
    </Box>
  );
};

export default PlanCards;
